/**
 * The HUD: resource bar, mission banner, radar, contact markers, weapon cards, view tabs
 * and the scope overlay.
 *
 * HTML and CSS rather than in-engine UI, per the plan: text stays crisp at any device
 * pixel ratio, both reference layouts fall out of media queries instead of two hand-built
 * compositions, and iterating on it costs a stylesheet reload rather than a shader.
 *
 * This layer knows nothing about Babylon. World positions arrive already projected to
 * screen space through the `project` callback, which keeps the renderer's types out of
 * the UI and means the HUD can be tested against a stub.
 */

import './hud.css';
import { Radar } from './radar';
import { contactLabel } from '../sim/detection';
import { VESSEL_SPECS } from '../content/vessels';
import { scopeFovDeg } from '../render/constants';
import { solveElevation } from '../sim/ballistics';
import {
  AUTOCANNON, TORPEDO, TORPEDO_MOUNT, TORPEDO_TUBES, DEPTH_CHARGE, GUN_MOUNT,
  PROJECTOR_MOUNT,
} from '../content/weapons';
import type { SimSnapshot, EntityId, WeaponId, Vessel } from '../sim/types';
import autocannonIconUrl from '../../30mm_icon.svg';
import torpedoIconUrl from '../../torpedo_icon.svg';
import depthChargeIconUrl from '../../depth_charge_icon.svg';

export type ViewMode = 'horizon' | 'scope';

const SVG_NS = 'http://www.w3.org/2000/svg';

const WEAPON_ICONS: Record<WeaponId, string> = {
  autocannon: autocannonIconUrl,
  torpedo: torpedoIconUrl,
  depthCharge: depthChargeIconUrl,
};

/**
 * The 30 mm ladder's calibration: for each range, the angle below the barrel axis at
 * which a waterline hull sits when the gun is laid to hit it.
 *
 * Computed from the real ballistics once at load — `solveElevation` gives the elevation
 * that connects, the line-of-sight angle comes from the mount height, and the holdover
 * is their difference. Put the tick for a target's range on the target and the centre of
 * the cross is elevated by exactly the drop; the panel's range readout turns the ladder
 * from engraving into an instrument. The old marks were drawn at even spacings that
 * corresponded to nothing, which is worse than no marks: a player who tried to use them
 * learned they lied.
 */
const LADDER = [1000, 1500, 2000, 2500, 3000].map((range) => {
  // Hull centre of a surface target sits a couple of metres above the waterline.
  const drop = 2 - GUN_MOUNT.y;
  const holdover = solveElevation(range, drop) - Math.atan2(drop, range);
  return { range, holdover };
});

/**
 * Pixels between the scope marker's caret and the top of the hull it points at.
 *
 * Held clear of the vessel rather than resting on it: at range the ship is only a few
 * pixels tall, and a marker sitting on it obscures the silhouette you are trying to read.
 */
const SCOPE_MARKER_GAP = 26;

/**
 * How long the trigger must be held before the fuse selector appears, milliseconds.
 *
 * Long enough that a quick tap is unambiguously a shot, short enough that reaching for
 * the fuse never feels like waiting. The projector's own cycle is four seconds, so this
 * costs nothing that can be noticed.
 */
const FUSE_HOLD_MS = 320;

/**
 * How long a tapped trigger is held down for, milliseconds.
 *
 * Long enough that the fixed-timestep simulation cannot miss it between ticks — a press
 * and release inside one 1/60 s gap is invisible to it, which is exactly how a tapped
 * depth charge came to throw nothing at all.
 */
const TAP_FIRE_MS = 80;

export interface ProjectedPoint {
  x: number;
  y: number;
  visible: boolean;
}

export interface HudCallbacks {
  onSelectContact: (id: EntityId) => void;
  onSetView: (view: ViewMode) => void;
  onSetWeapon: (weapon: WeaponId) => void;
  onFireDown: () => void;
  onFireUp: () => void;
  onToggleSpotlight: () => void;
  onHorn: () => void;
  onToggleInvert: () => void;
  /** Open the pause menu — the same route Escape takes. */
  onMenu: () => void;
  /** Cycle the depth-charge fuse: 10 / 25 / 45 m. */
  onCycleFuse: () => void;
  /** Set the fuse outright, from the touch selector. */
  onSetFuse: (depth: number) => void;
  onZoom: (magnification: number) => void;
  /** The radar sweep just painted a contact it had not seen before. */
  onContactAcquired: (hostile: boolean) => void;
  /** The four-control hold that opens the shift skipper. See `wireCheatGesture`. */
  onCheatGesture: () => void;
}

export interface HudFrame {
  snapshot: SimSnapshot;
  view: ViewMode;
  magnification: number;
  cameraYaw: number;
  /** Screen-space projection of a world point. */
  project: (x: number, y: number, z: number) => ProjectedPoint;
  /** Aim solution for the selected target, if any. */
  lead: { x: number; y: number; z: number; flightTime: number } | null;
  messages: ReadonlyArray<{ speaker: string; text: string; at: number }>;
  /** Sim time of the last refused unlocked torpedo shot; the scope flashes LOCK TARGET. */
  lockWarnAt: number | null;
  /** Sim time of the last confirmed 30 mm round into a hostile hull; the reticle ticks. */
  hullHitAt: number | null;
  /**
   * Where the projector's lob would land, world XZ — the aim ray from the mount
   * intersected with the sea. Null when the crosshair is on or above the horizon.
   */
  aimWater: { x: number; z: number } | null;
  /** 0 at solar noon, 1 at solar midnight — drives the illuminated-reticle tint. */
  nightFactor: number;
  /** True when the device is being driven by touch, which reveals the fire button. */
  touch: boolean;
  /** Vertical aim inverted for the current view. */
  inverted: boolean;
  /**
   * True while the pointer is captured by the scope. The HUD is unclickable in that
   * state, so anything the player might need has to name its keyboard route.
   */
  pointerLocked: boolean;
}

export class Hud {
  private readonly root: HTMLDivElement;
  private readonly radar: Radar;
  private readonly cb: HudCallbacks;

  private readonly els: {
    money: HTMLElement; oil: HTMLElement; integrity: HTMLElement;
    integrityBar: HTMLElement; crew: HTMLElement; crewBar: HTMLElement; ammo: HTMLElement;
    banner: HTMLElement; bannerText: HTMLElement;
    radioLog: HTMLElement;
    contactLayer: HTMLElement;
    scopeOverlay: HTMLElement; scopeInfo: HTMLElement;
    lockRing: SVGGElement; lockArc: SVGCircleElement;
    zoomControl: HTMLElement; zoomSlider: HTMLInputElement; zoomLabel: HTMLElement;
    alarm: HTMLElement;
    fireButton: HTMLElement;
    fuseSelector: HTMLElement;
    fireHint: HTMLElement;
    weaponNameFlash: HTMLElement;
    weaponCards: HTMLElement;
    viewTabs: HTMLElement;
    spotlight: HTMLElement;
    invert: HTMLElement;
  };

  /** Reused contact marker elements, keyed by entity, so we never thrash the DOM. */
  private readonly contactEls = new Map<EntityId, HTMLElement>();
  /**
   * Contacts the player has found through the optic, which HORIZON then keeps marked.
   *
   * The wide view's rule is that you cannot label what you cannot see, and that stands —
   * but only up to the moment the player has actually done the work of finding the thing.
   * Adam's direction, 2026-07-30: pick a blip off the radar, go to the scope, put the
   * sight on it and read its range, and it should not then be lost again on the way back
   * out. Losing it was the old behaviour, and it punished exactly the player who was
   * using the instruments properly.
   *
   * Cleared per contact the moment detection drops to nothing — a hull that goes back
   * over the horizon has to be found again — so this remembers work done, not a
   * permanent upgrade to the sensor.
   */
  private readonly scopeAcquired = new Set<EntityId>();
  private leadEl: HTMLElement;
  /** Which hull the pip is currently on, so a new acquisition can pulse. */
  private pipTargetId: EntityId | null = null;

  /**
   * Live weapon and fuse, mirrored from the last frame.
   *
   * The fuse selector is driven by pointer events, which arrive between frames and have
   * no snapshot of their own — so the two values it needs are kept here rather than being
   * read out of a frame that may be a beat stale.
   */
  private armedWeapon: WeaponId = 'autocannon';
  private armedFuse = DEPTH_CHARGE.fuseDepths[1] ?? 25;
  /** Non-null while the touch fuse selector is open; holds the depth under the finger. */
  private fusePick: number | null = null;
  private fuseHoldTimer: number | null = null;
  /**
   * True while a tapped charge is holding the trigger down for its few frames.
   *
   * The trigger releases through three events — `pointerup`, `pointercancel` and
   * `lostpointercapture` — and releasing the capture fires the third immediately after
   * the first. Without this the second pass through `release` found the tap already
   * handled, fell through to the ordinary path, and dropped the trigger in the same
   * millisecond it had just been raised. Measured: the charge never left.
   */
  private tapFiring = false;

  /** The per-weapon sight pictures: groups toggled by the armed weapon. */
  private readonly ret: {
    base: SVGGElement;
    g30: SVGGElement; torp: SVGGElement; dc: SVGGElement;
    dispersion: SVGCircleElement;
    ladder: SVGGElement; windage: SVGGElement;
    magArc: SVGPathElement; magCount: SVGTextElement; gunState: SVGTextElement;
    tape: SVGGElement; tapeReadout: SVGTextElement;
    tubePips: SVGGElement;
    fuseScale: SVGGElement;
  };

  /**
   * Element pools for the runtime-positioned reticle furniture. Ticks are repositioned
   * by attribute each frame rather than recreated — the bearing tape moves with every
   * mouse twitch, and churning forty SVG nodes at 60 fps is how a HUD becomes a GC
   * problem.
   */
  private readonly tapeTicks: SVGLineElement[] = [];
  private readonly tapeLabels: SVGTextElement[] = [];
  private readonly ladderTicks: SVGLineElement[] = [];
  private readonly ladderLabels: SVGTextElement[] = [];
  private readonly windageTicks: SVGLineElement[] = [];
  private readonly pipEls: SVGRectElement[] = [];
  private fuseChevron!: SVGPathElement;
  private fuseLabelEls: SVGTextElement[] = [];

  /** Projected-world overlays: the projector's impact point, the torpedo's run line. */
  private dcImpact!: HTMLElement;
  private trackSvg!: SVGSVGElement;
  private trackLine!: SVGPolylineElement;

  /** Magnification the angular furniture was last laid out for; 0 forces the first. */
  private lastMag = 0;
  private lastHitSeen: number | null = null;

  private lastBanner = '';
  private bannerTimer = 0;
  private renderedMessages = 0;

  constructor(parent: HTMLElement, callbacks: HudCallbacks) {
    this.cb = callbacks;

    this.root = document.createElement('div');
    this.root.id = 'hud';
    this.root.innerHTML = TEMPLATE;
    parent.appendChild(this.root);

    const q = <T extends HTMLElement>(sel: string): T => {
      const el = this.root.querySelector<T>(sel);
      if (!el) throw new Error(`hud: missing element ${sel}`);
      return el;
    };

    this.els = {
      money: q('#resMoney'),
      oil: q('#resOil'),
      integrity: q('#resIntegrity'),
      integrityBar: q('#resIntegrityBar'),
      crew: q('#resCrew'),
      crewBar: q('#resCrewBar'),
      ammo: q('#resAmmo'),
      banner: q('#missionBanner'),
      bannerText: q('#missionBannerText'),
      radioLog: q('#radioLog'),
      contactLayer: q('#contactLayer'),
      scopeOverlay: q('#scopeOverlay'),
      scopeInfo: q('#scopeInfo'),
      // SVG nodes, so they bypass q()'s HTMLElement bound.
      lockRing: this.root.querySelector('#lockRing') as SVGGElement,
      lockArc: this.root.querySelector('#lockArc') as SVGCircleElement,
      zoomControl: q('#zoomControl'),
      zoomSlider: q('#zoomSlider'),
      zoomLabel: q('#zoomLabel'),
      alarm: q('#alarmPulse'),
      fireButton: q('#fireButton'),
      fuseSelector: q('#fuseSelector'),
      fireHint: q('#fireButton .fireHint'),
      weaponNameFlash: q('#weaponNameFlash'),
      weaponCards: q('#weaponCards'),
      viewTabs: q('#viewTabs'),
      spotlight: q('#utilSpotlight'),
      invert: q('#utilInvert'),
    };

    this.radar = new Radar({
      canvas: q<HTMLCanvasElement>('#radar'),
      onAcquire: (hostile) => this.cb.onContactAcquired(hostile),
    });

    this.leadEl = document.createElement('div');
    this.leadEl.className = 'leadPip';
    this.leadEl.style.display = 'none';
    this.els.contactLayer.appendChild(this.leadEl);

    const sq = <T extends SVGElement>(sel: string): T => {
      const el = this.root.querySelector<T>(sel);
      if (!el) throw new Error(`hud: missing reticle element ${sel}`);
      return el;
    };
    this.ret = {
      base: sq('#retBase'),
      g30: sq('#ret30'), torp: sq('#retTorp'), dc: sq('#retDC'),
      dispersion: sq('#dispersionRing'),
      ladder: sq('#ladder'), windage: sq('#windage'),
      magArc: sq('#magArc'), magCount: sq('#magCount'), gunState: sq('#gunState'),
      tape: sq('#tape'), tapeReadout: sq('#tapeReadout'),
      tubePips: sq('#tubePips'),
      fuseScale: sq('#fuseScale'),
    };
    this.buildReticlePools();

    this.wireEvents();
  }

  /** One-time construction of the pooled reticle furniture and world-space overlays. */
  private buildReticlePools(): void {
    const make = <K extends keyof SVGElementTagNameMap>(
      tag: K, parent: SVGElement,
    ): SVGElementTagNameMap[K] => {
      const el = document.createElementNS(SVG_NS, tag);
      parent.appendChild(el);
      return el;
    };

    // Bearing tape: enough ticks for the widest view (2x spans ~±16° at 5° steps — but
    // the pool is sized for the finest, 8x at 1° steps across ~±4° plus margin).
    for (let i = 0; i < 44; i++) {
      const tick = make('line', this.ret.tape);
      tick.setAttribute('visibility', 'hidden');
      this.tapeTicks.push(tick);
    }
    for (let i = 0; i < 10; i++) {
      const label = make('text', this.ret.tape);
      label.setAttribute('text-anchor', 'middle');
      label.setAttribute('visibility', 'hidden');
      this.tapeLabels.push(label);
    }

    // Holdover ladder and windage ticks — positions written per magnification.
    for (const step of LADDER) {
      const tick = make('line', this.ret.ladder);
      this.ladderTicks.push(tick);
      const label = make('text', this.ret.ladder);
      label.setAttribute('text-anchor', 'start');
      label.textContent = step.range % 1000 === 0 ? `${step.range / 1000}` : '';
      this.ladderLabels.push(label);
    }
    for (let i = 0; i < 4; i++) this.windageTicks.push(make('line', this.ret.windage));

    /*
     * Tube pips, centred under the lock ring.
     *
     * Built for the largest capacity the rig can have rather than the stock pair, since
     * the extra tube pair is fitted between shifts and the row cannot be rebuilt then.
     * Pips past the current capacity are hidden per frame.
     */
    const maxTubes = TORPEDO_TUBES + 2;
    const pipSpan = maxTubes * 2.3 - 0.6;
    for (let i = 0; i < maxTubes; i++) {
      const pip = make('rect', this.ret.tubePips);
      pip.setAttribute('x', String(50 - pipSpan / 2 + i * 2.3));
      pip.setAttribute('y', '57.7');
      pip.setAttribute('width', '1.7');
      pip.setAttribute('height', '1.7');
      pip.setAttribute('rx', '0.2');
      this.pipEls.push(pip);
    }

    // Fuse-depth scale: spine, a tick and numeral per selectable depth, a chevron that
    // rides the current selection, and the key that changes it. The whole scale sits
    // below the horizontal cross line — straddling it put the 25 m numeral on the line.
    const fuseY = (d: number): number => 52.3 + (d / 45) * 6.4;
    const depths = DEPTH_CHARGE.fuseDepths;
    const spine = make('line', this.ret.fuseScale);
    spine.setAttribute('x1', '42.6'); spine.setAttribute('x2', '42.6');
    spine.setAttribute('y1', String(fuseY(depths[0] as number) - 1));
    spine.setAttribute('y2', String(fuseY(depths[depths.length - 1] as number) + 1));
    for (const d of depths) {
      const tick = make('line', this.ret.fuseScale);
      tick.setAttribute('x1', '41.9'); tick.setAttribute('x2', '42.6');
      tick.setAttribute('y1', String(fuseY(d))); tick.setAttribute('y2', String(fuseY(d)));
      const label = make('text', this.ret.fuseScale);
      label.setAttribute('x', '41.1');
      label.setAttribute('y', String(fuseY(d) + 0.55));
      label.setAttribute('text-anchor', 'end');
      label.textContent = String(d);
      this.fuseLabelEls.push(label);
    }
    const caption = make('text', this.ret.fuseScale);
    caption.setAttribute('x', '41.1');
    caption.setAttribute('y', String(fuseY(depths[depths.length - 1] as number) + 3));
    caption.setAttribute('text-anchor', 'end');
    caption.classList.add('caption');
    caption.textContent = 'FUSE (F)';
    this.fuseChevron = make('path', this.ret.fuseScale);
    this.fuseChevron.setAttribute('d', 'M 43.2 0 l 1.5 -0.85 l 0 1.7 Z');

    // The projector's impact marker and the torpedo's run line are projected world
    // points, so they live with the contact markers and inherit the scope's clip.
    this.dcImpact = document.createElement('div');
    this.dcImpact.id = 'dcImpact';
    this.dcImpact.innerHTML =
      '<div class="ring"></div><div class="label"><span class="r"></span><span class="s"></span></div>';
    this.dcImpact.style.display = 'none';
    this.els.contactLayer.appendChild(this.dcImpact);

    this.trackSvg = document.createElementNS(SVG_NS, 'svg');
    this.trackSvg.id = 'trackSvg';
    this.trackSvg.style.display = 'none';
    this.trackLine = document.createElementNS(SVG_NS, 'polyline');
    this.trackSvg.appendChild(this.trackLine);
    this.els.contactLayer.appendChild(this.trackSvg);
  }

  /**
   * Adam's way into the shift skipper, on a device with no console.
   *
   * Hold SCOPE, FIRE, 30 MM and DEPTH CHG together for three seconds. Four simultaneous
   * touches on four controls that are nowhere near each other is not something a hand
   * does by accident, which is the whole requirement: this ships to production, where the
   * only other route to a late shift is playing eleven of them.
   *
   * Observers only — no `preventDefault`, no `stopPropagation`. Every one of these
   * controls keeps doing its own job while the gesture is held, which is why the skipper
   * hands out a full magazine: by the time the menu opens the player has switched weapons
   * twice and held the trigger down for three seconds.
   *
   * Releases are watched on `window` rather than per control. The fire button captures
   * its own pointer and the cards sit inside a flex row, so a finger that slides off the
   * one it landed on would otherwise leave a hold that never ends.
   */
  private wireCheatGesture(): void {
    const controls: Array<[string, HTMLElement | null]> = [
      ['scope', this.els.viewTabs.querySelector('[data-view="scope"]')],
      ['fire', this.els.fireButton],
      ['autocannon', this.els.weaponCards.querySelector('[data-weapon="autocannon"]')],
      ['depthCharge', this.els.weaponCards.querySelector('[data-weapon="depthCharge"]')],
    ];
    const wanted = controls.length;
    /** Which control each live pointer is holding, so a release knows what it freed. */
    const holding = new Map<number, string>();
    let timer: number | null = null;

    const cancel = (): void => {
      if (timer !== null) { window.clearTimeout(timer); timer = null; }
    };

    const review = (): void => {
      const distinct = new Set(holding.values());
      if (distinct.size >= wanted) {
        if (timer === null) {
          timer = window.setTimeout(() => { timer = null; this.cb.onCheatGesture(); }, 3000);
        }
        return;
      }
      cancel();
    };

    for (const [name, el] of controls) {
      el?.addEventListener('pointerdown', (e) => {
        holding.set((e as PointerEvent).pointerId, name);
        review();
      });
    }
    const release = (e: PointerEvent): void => {
      if (holding.delete(e.pointerId)) review();
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    // Losing the window drops every finger at once.
    window.addEventListener('blur', () => { holding.clear(); cancel(); });
  }

  private wireEvents(): void {
    this.wireCheatGesture();
    this.els.viewTabs.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('button');
      if (!btn || btn.hasAttribute('disabled')) return;
      const view = btn.dataset['view'];
      if (view === 'horizon' || view === 'scope') this.cb.onSetView(view);
    });

    this.els.weaponCards.addEventListener('click', (e) => {
      const btn = (e.target as HTMLElement).closest('button');
      if (!btn || btn.hasAttribute('disabled')) return;
      const w = btn.dataset['weapon'] as WeaponId | undefined;
      if (w) {
        this.cb.onSetWeapon(w);
        this.flashWeaponName(w);
      }
    });

    // Fire uses independent pointer capture so a second finger can keep aiming, and it
    // releases on every path that could otherwise leave the gun stuck on.
    const fire = this.els.fireButton;
    fire.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      fire.setPointerCapture(e.pointerId);
      /*
       * The projector's fuse lives behind a hold on this button.
       *
       * There is no other way to set it on a phone: the depth selector is the F key, and
       * a phone has no keys — so the one weapon whose whole skill is choosing a depth
       * shipped with its depth un-choosable. Adam's fix, and his gesture.
       *
       * The lob is deferred until release rather than fired on the way in, because a
       * charge already in the air makes the selector meaningless for the shot the player
       * is taking. It costs `FUSE_HOLD_MS` of latency on a weapon with a four-second
       * cycle, which is nothing, and only in this mode — the gun and the tube still fire
       * the instant the finger lands.
       */
      if (this.armedWeapon === 'depthCharge') {
        this.fuseHoldTimer = window.setTimeout(() => {
          this.fuseHoldTimer = null;
          this.openFuseSelector();
        }, FUSE_HOLD_MS);
        return;
      }
      this.cb.onFireDown();
    });

    fire.addEventListener('pointermove', (e) => {
      if (this.fusePick === null) return;
      e.preventDefault();
      this.moveFuseSelector(e.clientY);
    });

    const release = (): void => {
      if (this.tapFiring) return;
      if (this.fuseHoldTimer !== null) {
        /*
         * Released before the selector opened: that was a tap, so throw the charge.
         *
         * The release is deferred a few frames. Pressing and releasing in the same gap
         * between simulation ticks sets the trigger and clears it again without the sim
         * ever observing it down, so the charge was never thrown — measured, on a real
         * tap. The projector fires once per press regardless of how long the trigger is
         * held, so the extra frames cost nothing but the certainty.
         */
        window.clearTimeout(this.fuseHoldTimer);
        this.fuseHoldTimer = null;
        this.tapFiring = true;
        this.cb.onFireDown();
        window.setTimeout(() => {
          this.tapFiring = false;
          this.cb.onFireUp();
        }, TAP_FIRE_MS);
        return;
      }
      if (this.fusePick !== null) {
        // Released on the selector: take the depth, and deliberately do not fire.
        this.cb.onSetFuse(this.fusePick);
        this.closeFuseSelector();
        return;
      }
      this.cb.onFireUp();
    };
    fire.addEventListener('pointerup', release);
    fire.addEventListener('pointercancel', release);
    fire.addEventListener('lostpointercapture', release);

    this.els.spotlight.addEventListener('click', () => this.cb.onToggleSpotlight());
    this.els.invert.addEventListener('click', () => this.cb.onToggleInvert());
    this.root.querySelector('#utilHorn')?.addEventListener('click', () => this.cb.onHorn());
    this.root.querySelector('#utilMenu')?.addEventListener('click', () => this.cb.onMenu());

    this.els.zoomSlider.addEventListener('input', () => {
      this.cb.onZoom(Number(this.els.zoomSlider.value));
    });
  }

  /** Briefly name a tapped mobile weapon without permanently spending screen space. */
  private flashWeaponName(weapon: WeaponId): void {
    const labels: Record<WeaponId, string> = {
      autocannon: '30 mm',
      torpedo: 'Torpedo',
      depthCharge: 'Depth charge',
    };
    this.els.weaponNameFlash.textContent = labels[weapon];
    this.els.weaponNameFlash.dataset['weapon'] = weapon;
    // Removing and restoring the class restarts the 750 ms animation when the same
    // weapon is tapped twice in a row.
    this.els.weaponNameFlash.classList.remove('show');
    void this.els.weaponNameFlash.offsetWidth;
    this.els.weaponNameFlash.classList.add('show');
  }

  /**
   * The touch fuse selector: the three depths stacked above the trigger, deepest at the
   * bottom, so dragging *down* selects deeper. Slid open under the thumb that is already
   * on the button, which is the only finger guaranteed to be free.
   */
  private openFuseSelector(): void {
    const depths = DEPTH_CHARGE.fuseDepths;
    this.fusePick = this.armedFuse;
    this.els.fuseSelector.replaceChildren();
    for (const d of depths) {
      const row = document.createElement('div');
      row.className = 'fuseOpt';
      row.dataset['depth'] = String(d);
      row.textContent = `${d} m`;
      this.els.fuseSelector.appendChild(row);
    }
    this.els.fuseSelector.classList.add('show');
    this.markFusePick();
    this.haptic();
  }

  /** Which row the finger is over. Outside the strip, the nearest end wins. */
  private moveFuseSelector(clientY: number): void {
    const rows = Array.from(
      this.els.fuseSelector.querySelectorAll<HTMLElement>('.fuseOpt'),
    );
    if (rows.length === 0) return;
    let best = rows[0] as HTMLElement;
    let bestDist = Infinity;
    for (const row of rows) {
      const r = row.getBoundingClientRect();
      const dist = Math.abs(clientY - (r.top + r.height / 2));
      if (dist < bestDist) { bestDist = dist; best = row; }
    }
    const picked = Number(best.dataset['depth']);
    if (picked === this.fusePick) return;
    this.fusePick = picked;
    this.markFusePick();
    this.haptic();
  }

  private markFusePick(): void {
    for (const row of this.els.fuseSelector.querySelectorAll<HTMLElement>('.fuseOpt')) {
      row.classList.toggle('on', Number(row.dataset['depth']) === this.fusePick);
    }
  }

  private closeFuseSelector(): void {
    this.fusePick = null;
    this.els.fuseSelector.classList.remove('show');
  }

  /** A short tick as the selection moves, where the device has a motor. */
  private haptic(): void {
    try { navigator.vibrate?.(8); } catch { /* unsupported; the highlight is the feedback */ }
  }

  setSpotlightState(on: boolean): void {
    this.els.spotlight.classList.toggle('on', on);
  }

  /**
   * Tell the HUD the tutorial prompt is occupying the bottom-centre band.
   *
   * The prompt lives outside the HUD element, so CSS cannot see it from here — and it
   * sits exactly where the radio log's first lines print. This is what lets the log step
   * over it for the duration rather than being written underneath it.
   */
  setTutorialActive(active: boolean): void {
    this.root.classList.toggle('tutorial', active);
  }

  /**
   * Reflect vertical-aim inversion on the button.
   *
   * The label names the view it currently applies to, because the two are independent and
   * a single unlabelled "INV" would leave you guessing which one you just flipped.
   */
  setInvertState(inverted: boolean, inScope: boolean): void {
    this.els.invert.classList.toggle('on', inverted);
    this.els.invert.innerHTML = `${inScope ? 'inv scope' : 'inv look'} <b>(w)</b>`;
    this.els.invert.setAttribute(
      'title',
      `Invert vertical aim in the ${inScope ? 'scope' : 'wide view'} (I)`,
    );
  }

  show(): void { this.root.classList.remove('hidden'); }
  hide(): void { this.root.classList.add('hidden'); }

  /**
   * Clear the radio log and forget how much of it has been drawn.
   *
   * The HUD outlives a shift but the message buffer does not — starting one builds a
   * fresh `SimRenderSync` with an empty list, while this element kept the previous run's
   * lines and `renderedMessages` kept the previous run's count. The count then sat above
   * the new list's length, so the append loop never ran and the stale lines stayed up:
   * the first thing a new player read was the last thing the *previous* session said,
   * printed twice. Called whenever a shift starts.
   */
  resetRadio(): void {
    this.els.radioLog.replaceChildren();
    this.renderedMessages = 0;
    this.updateRadioPresence();
  }

  /**
   * Hide the subtitle strip while nobody is saying anything.
   *
   * On a phone the log is a panel — background, amber edge, its own minimum height — and
   * an empty one sat on screen for the whole shift looking like a readout that had failed
   * rather than a channel that was quiet. The strip belongs to the words: it arrives with
   * them and leaves with them.
   *
   * Driven off the DOM rather than off `messages.length` because the lines remove
   * themselves on their own timers, nine seconds after they print — the message buffer
   * still holds every line of the shift long after the last one has left the screen.
   */
  private updateRadioPresence(): void {
    const speaking = Array.from(this.els.radioLog.children)
      .some((el) => !el.classList.contains('fading'));
    this.els.radioLog.classList.toggle('quiet', !speaking);
  }

  update(frame: HudFrame, dt: number): void {
    const { snapshot: s } = frame;

    this.updateResources(frame);
    this.updateBanner(frame, dt);
    this.updateRadio(frame);
    this.updateContacts(frame);
    this.updateWeapons(frame);
    this.updateScope(frame);

    this.armedFuse = s.rig.fuseDepth;
    if (s.rig.selectedWeapon !== this.armedWeapon) {
      this.armedWeapon = s.rig.selectedWeapon;
      // Switching weapons mid-hold abandons the selection rather than applying it.
      if (this.fusePick !== null) this.closeFuseSelector();
      /*
       * The trigger says what a hold actually does.
       *
       * With the projector armed a hold opens the fuse selector rather than sustaining
       * fire, so "hold to engage" was describing the wrong weapon — and it is the one
       * mode where the hold is the whole point, which is why it is set brighter and
       * larger than the ordinary hint rather than merely reworded.
       */
      const projector = this.armedWeapon === 'depthCharge';
      this.els.fireHint.textContent = projector ? 'hold for depth' : 'hold to engage';
      this.els.fireHint.classList.toggle('depth', projector);
    }

    this.radar.draw(s.vessels, s.selectedId, frame.cameraYaw, dt);

    this.root.classList.toggle('scoped', frame.view === 'scope');
    this.els.contactLayer.classList.toggle('scoped', frame.view === 'scope');
    this.els.alarm.classList.toggle('on', s.rig.alarmTimer > 0);
    /*
     * The trigger follows the gun, not the optic (Adam, 2026-07-30).
     *
     * It used to exist only inside the scope, on the reasoning that HORIZON was a survey
     * view where the gun answered no input at all. That reasoning was sound and the
     * conclusion was wrong: the wide view carries its own crosshair, the player aims with
     * it, and on a phone there was then nothing to pull. So the button shows here too —
     * but only with the autocannon up, because it is the one weapon that shoots where the
     * wide view is pointed. The torpedo needs a lock the optic builds and the projector
     * needs a range the player reads off the water, and both stay scope-side.
     */
    const gunUp = s.rig.selectedWeapon === 'autocannon';
    this.els.fireButton.classList.toggle(
      'touch',
      frame.touch && (frame.view === 'scope' || gunUp),
    );
    this.els.zoomControl.classList.toggle('show', frame.view === 'scope');
  }

  // --- Sections -------------------------------------------------------------------------

  private updateResources(frame: HudFrame): void {
    const r = frame.snapshot.resources;
    const e = this.els;

    e.money.textContent = `$${Math.round(r.money).toLocaleString()}`;
    e.oil.textContent = `${Math.round(r.oil).toLocaleString()}`;

    const integrity = Math.round(r.integrity);
    e.integrity.textContent = `${integrity}%`;
    e.integrity.className = `value mono ${integrity < 25 ? 'crit' : integrity < 50 ? 'warn' : ''}`;
    e.integrityBar.style.width = `${Math.max(0, integrity)}%`;
    (e.integrityBar.parentElement as HTMLElement).className =
      `bar ${integrity < 25 ? 'crit' : integrity < 50 ? 'warn' : ''}`;

    const crewPct = (r.crew / r.crewCapacity) * 100;
    e.crew.textContent = `${Math.round(r.crew)}/${r.crewCapacity}`;
    e.crew.className = `value mono ${r.crew < 30 ? 'crit' : r.crew < 60 ? 'warn' : ''}`;
    e.crewBar.style.width = `${Math.max(0, crewPct)}%`;
    (e.crewBar.parentElement as HTMLElement).className =
      `bar ${r.crew < 30 ? 'crit' : r.crew < 60 ? 'warn' : ''}`;

    const mag = frame.snapshot.rig.magazine;
    e.ammo.textContent = `${mag.toLocaleString()}`;
    e.ammo.className = `value mono ${mag < 120 ? 'crit' : mag < 400 ? 'warn' : ''}`;
  }

  private updateBanner(frame: HudFrame, dt: number): void {
    const objective = frame.snapshot.shift.objective;
    if (objective && objective !== this.lastBanner) {
      this.lastBanner = objective;
      this.els.bannerText.textContent = objective;
      this.els.banner.classList.add('show');
      this.bannerTimer = 6;
    }
    if (this.bannerTimer > 0) {
      this.bannerTimer -= dt;
      if (this.bannerTimer <= 0) this.els.banner.classList.remove('show');
    }
  }

  private updateRadio(frame: HudFrame): void {
    if (frame.messages.length === this.renderedMessages) return;

    // Only append what is new, and keep the visible window short — the log is glanceable
    // colour, not something the player is expected to read in full.
    for (let i = this.renderedMessages; i < frame.messages.length; i++) {
      const m = frame.messages[i];
      if (!m) continue;
      const line = document.createElement('div');
      line.className = 'line';
      line.innerHTML = `<span class="who">${escapeHtml(m.speaker)}</span>`
        + `<span class="what">${escapeHtml(m.text)}</span>`;
      this.els.radioLog.appendChild(line);

      window.setTimeout(() => {
        line.classList.add('fading');
        this.updateRadioPresence();
        window.setTimeout(() => {
          line.remove();
          this.updateRadioPresence();
        }, 1400);
      }, 9000);
    }
    this.renderedMessages = frame.messages.length;

    while (this.els.radioLog.childElementCount > 5) {
      this.els.radioLog.firstElementChild?.remove();
    }
    this.updateRadioPresence();
  }

  /**
   * Draw a bracket and a distance chip over every contact the player can actually see.
   *
   * Radar-only contacts get nothing here — they exist on the radar and nowhere else,
   * which is exactly the tension the detection model is for: you know something is out
   * there, and you have to look for it.
   */
  private updateContacts(frame: HudFrame): void {
    const s = frame.snapshot;
    const scoped = frame.view === 'scope';
    const alive = new Set<EntityId>();

    for (const v of s.vessels) {
      if (v.detection === 'undetected') {
        // Gone from the sensors entirely: whatever the optic found is no longer found.
        this.scopeAcquired.delete(v.id);
        continue;
      }
      if (v.state === 'dead') {
        this.scopeAcquired.delete(v.id);
        continue;
      }

      /*
       * In the wide view a contact must be visible to be marked — the whole point of the
       * detection ladder is that you cannot label what you cannot see.
       *
       * The scope is different, and deliberately so. Creative direction is that vessels
       * need not be legible at long range: the radar says something is out there and the
       * optic tells you how far. So a radar-only contact still gets a range readout here,
       * even when the hull itself has dissolved into the weather. The optic is an
       * instrument, not just a magnifier.
       */
      if (!scoped && v.detection === 'radar' && !this.scopeAcquired.has(v.id)) continue;

      const spec = VESSEL_SPECS[v.kind];
      // Anchor on the masthead in the scope and amidships in the wide view: the scope
      // marker hangs clear above the vessel, the wide-view bracket frames it.
      const anchorY = v.pos.y + spec.height * (scoped ? 1 : 0.6);
      const p = frame.project(v.pos.x, anchorY, v.pos.z);
      if (!p.visible) continue;

      alive.add(v.id);
      // Found through the optic: past the projection test, so this is the exact moment
      // the range readout appears over the contact. HORIZON keeps it from here on.
      if (scoped) this.scopeAcquired.add(v.id);

      let el = this.contactEls.get(v.id);
      if (!el) {
        el = document.createElement('div');
        el.className = 'contact';
        el.innerHTML = '<span class="bracket"></span>'
          + '<span class="range"></span><span class="ptr"></span>'
          + '<span class="chip"></span>';
        el.addEventListener('pointerdown', (ev) => {
          ev.stopPropagation();
          this.cb.onSelectContact(v.id);
        });
        this.els.contactLayer.appendChild(el);
        this.contactEls.set(v.id, el);
      }

      const range = Math.hypot(v.pos.x, v.pos.z);
      /*
       * A killed hull reads SUNK rather than a distance, for as long as it is still
       * there to look at (Adam's direction).
       *
       * `sinking` is the whole dying animation — burning on the surface, then settling —
       * and it ends exactly when the hull is under, so the word lasts precisely as long
       * as there is something in the water to explain. How far away a wreck is stopped
       * being useful the moment it stopped being a target; whether it is finished has
       * not. The colour comes from the `sinking` class, which the stylesheet orders last
       * so it outranks selection and the torpedo lock alike.
       */
      const rangeEl = el.querySelector('.range');
      if (rangeEl) {
        rangeEl.textContent = v.state === 'sinking' ? 'SUNK' : formatRange(range);
      }

      /*
       * The torpedo's chosen hull, marked on the hull itself.
       *
       * The lock ring at the centre of the sight says a lock is building; it cannot say
       * on WHAT, and with two hostiles on close bearings the player was launching into
       * an ambiguity. Only while the tube is the selected weapon — this is a torpedo
       * readout, and it has no business colouring the display when the gun is up.
       */
      const lockTarget = s.rig.selectedWeapon === 'torpedo'
        && s.torpedoLock.targetId === v.id
        ? (s.torpedoLock.locked ? ' lock-on' : ' lock-acquiring')
        : '';

      /*
       * The scope suppresses labels to keep ink off the water. The hull about to take a
       * torpedo is the exception: naming it is the whole point of marking it.
       *
       * HORIZON has no distance readout — this chip is its one line of text — so a
       * killed hull says SUNK here instead of its class, which is the same substitution
       * the scope makes on its range. What it is stopped mattering when it died; the
       * sinking branch cannot collide with the torpedo one, since a sinking hull is not
       * lockable.
       */
      const chip = el.querySelector('.chip');
      if (chip) {
        chip.textContent = scoped && !lockTarget
          ? ''
          : v.state === 'sinking' ? 'SUNK' : contactLabel(v);
      }

      // The bracket frames the hull, so it shrinks with distance. In the scope it gets
      // out of the way entirely — the pointer and the range are the readout.
      const bracket = el.querySelector<HTMLElement>('.bracket');
      if (bracket) {
        const size = clamp((spec.length / Math.max(range, 1)) * 2600, 18, 120);
        bracket.style.width = `${size}px`;
        bracket.style.height = `${size}px`;
      }

      /*
       * Three different things are known at three different moments, so they are gated
       * differently rather than all waiting on identification.
       *
       * Allegiance comes from IFF and is known from first radar contact — it matches what
       * the acquisition tone already told the player, and the radar blip's colour.
       * Sinking is plainly observable: a hull going down looks like a hull going down at
       * any range. Only the half-strength warning needs a real look at the target, since
       * judging how much fight is left in something is exactly what identification is.
       *
       * Together the marker answers "is this still worth a burst?" without the player
       * opening the target panel — which, in the scope, means without looking away from
       * the thing they are shooting at.
       */
      const condition = v.state === 'sinking' ? ' sinking'
        : (v.detection === 'identified' && v.hp <= v.maxHp * 0.5) ? ' damaged' : '';

      el.className = 'contact'
        + (scoped ? ' scoped' : '')
        + (v.faction === 'hostile' ? ' hostile' : ' friendly')
        + condition
        + lockTarget
        + (v.id === s.selectedId ? ' selected' : '');
      // Centred in the wide view; lifted clear of the hull in the scope so the pointer
      // touches the top of the ship rather than covering it.
      el.style.transform = scoped
        ? `translate(-50%, -100%) translate(${p.x}px, ${p.y - SCOPE_MARKER_GAP}px)`
        : `translate(-50%, -50%) translate(${p.x}px, ${p.y}px)`;
    }

    for (const [id, el] of this.contactEls) {
      if (alive.has(id)) continue;
      el.remove();
      this.contactEls.delete(id);
    }

    /*
     * Drop acquisitions for hulls that are no longer in the world at all.
     *
     * The per-contact clears above only fire for vessels still in the snapshot, and a
     * shift change removes every vessel *and* restarts entity ids from one — so without
     * this, the second shift's first contact would inherit the first shift's acquisition
     * and appear on HORIZON having never been looked at.
     */
    if (this.scopeAcquired.size > 0) {
      const present = new Set(s.vessels.map((v) => v.id));
      for (const id of this.scopeAcquired) {
        if (!present.has(id)) this.scopeAcquired.delete(id);
      }
    }

    /*
     * Lead pip.
     *
     * Arrives on the gun's own aim-lock rather than on the selection — the crosshair
     * settles on a hull for its dwell and the solution appears. It pulses on the way in,
     * on Adam's direction: the moment a solution becomes real is the moment the player has
     * been waiting through the dwell for, and a marker that simply materialises is easy to
     * miss at 8× with a boat moving under it.
     */
    const lockedOn = frame.snapshot.gunLock.locked ? frame.snapshot.gunLock.targetId : null;
    if (lockedOn !== this.pipTargetId) {
      this.pipTargetId = lockedOn;
      // Restart the animation, so re-acquiring a second hull pulses again.
      this.leadEl.classList.remove('acquired');
      if (lockedOn !== null) {
        void this.leadEl.offsetWidth;
        this.leadEl.classList.add('acquired');
      }
    }

    if (frame.lead && frame.view === 'scope') {
      const p = frame.project(frame.lead.x, frame.lead.y, frame.lead.z);
      if (p.visible) {
        this.leadEl.style.display = 'block';
        this.leadEl.style.left = `${p.x}px`;
        this.leadEl.style.top = `${p.y}px`;
      } else {
        this.leadEl.style.display = 'none';
      }
    } else {
      this.leadEl.style.display = 'none';
    }
  }

  private updateWeapons(frame: HudFrame): void {
    const s = frame.snapshot;
    const cards = this.els.weaponCards.querySelectorAll<HTMLButtonElement>('.weaponCard');
    for (const card of cards) {
      const id = card.dataset['weapon'] as WeaponId;
      const active = s.rig.selectedWeapon === id;
      card.classList.toggle('active', active);

      const ammoEl = card.querySelector('.ammo');
      const badge = card.querySelector('.badge');
      if (id === 'autocannon') {
        if (ammoEl) ammoEl.textContent = `${s.rig.magazine}`;
        if (badge) {
          badge.textContent = s.rig.reloadTimer > 0
            ? `reloading ${s.rig.reloadTimer.toFixed(1)}s`
            : s.rig.magazine === 0 ? 'empty'
              : `${s.resources.ammoReserve} reserve`;
        }
        card.disabled = false;
      } else if (id === 'torpedo') {
        if (ammoEl) ammoEl.textContent = `${s.resources.torpedoes}`;
        if (badge) {
          /*
           * Three different reasons a tube may not answer, and the player needs to know
           * which. Interlock is seconds away; a rack reload is half a minute; empty is a
           * trip to the requisition screen. Collapsing them into "unavailable" would make
           * the weapon feel broken rather than busy.
           */
          badge.textContent = s.resources.torpedoes <= 0 ? 'empty'
            : s.rig.torpedoInterlock > 0 ? `interlock ${s.rig.torpedoInterlock.toFixed(1)}s`
              : s.rig.torpedoTubes <= 0 ? `racking ${Math.ceil(s.rig.torpedoRackTimer)}s`
                : frame.touch
                  ? `${s.rig.torpedoTubes} torpedo${s.rig.torpedoTubes === 1 ? '' : 'es'} ready`
                  : `${s.rig.torpedoTubes} tube${s.rig.torpedoTubes === 1 ? '' : 's'}`;
        }
        card.disabled = s.resources.torpedoes <= 0;
      } else {
        if (ammoEl) ammoEl.textContent = `${s.resources.depthCharges}`;
        if (badge) {
          badge.textContent = s.resources.depthCharges <= 0 ? 'empty'
            : s.rig.projectorSwapTimer > 0 ? `swap ${s.rig.projectorSwapTimer.toFixed(1)}s`
              : `fuse ${s.rig.fuseDepth} m  ·  ${s.rig.projectorMagazine} in rack`;
        }
        card.disabled = s.resources.depthCharges <= 0;
      }
    }

    for (const tab of this.els.viewTabs.querySelectorAll<HTMLButtonElement>('.viewTab')) {
      tab.classList.toggle('active', tab.dataset['view'] === frame.view);
    }
  }

  /** The target-data panel: what the optic can tell you about what you are looking at. */
  private updateScope(frame: HudFrame): void {
    const showing = frame.view === 'scope';
    this.els.scopeOverlay.classList.toggle('show', showing);
    if (!showing) {
      // The projected-world overlays live in the contact layer, outside the scope
      // element, so leaving the scope must put them away explicitly.
      this.dcImpact.style.display = 'none';
      this.trackSvg.style.display = 'none';
      return;
    }

    this.els.zoomLabel.textContent = `${frame.magnification.toFixed(0)}×`;
    if (Number(this.els.zoomSlider.value) !== frame.magnification) {
      this.els.zoomSlider.value = String(frame.magnification);
    }

    /*
     * After dark the glass warms to a dim illuminated amber — the treatment a real
     * optic gets so the etching stays readable without wrecking dark adaptation, and it
     * keeps the reticle from glowing white against a black sea.
     */
    this.els.scopeOverlay.classList.toggle('night', frame.nightFactor > 0.55);

    this.updateReticle(frame);

    /*
     * The torpedo's lock ring. Hidden for the other weapons; a growing arc while the
     * crosshair dwells on a hull; a solid ring, ticks and label once locked — the
     * "you may fire" signal, since the tube refuses an unlocked launch.
     */
    const lock = frame.snapshot.torpedoLock;
    const rig = frame.snapshot.rig;
    const torpedoArmed = rig.selectedWeapon === 'torpedo';
    // With the inventory empty the reticle disappears whole: a lock UI over a dry tube
    // is a promise the weapon cannot keep. LOADING covers every state where the tube
    // will not accept a launch right now but will again — the 8 s interlock after each
    // shot as much as the 30 s rack refill — and while it shows, the lock visuals stand
    // down entirely (Adam: no SHOOT, no LOCK, just LOADING). The lock itself keeps
    // building underneath, so the shot is live the moment the weapon is.
    const out = frame.snapshot.resources.torpedoes <= 0;
    const loading = !out && (rig.torpedoTubes <= 0 || rig.torpedoInterlock > 0);
    const show = torpedoArmed && !out;
    this.els.lockRing.classList.toggle('show', show);
    if (show) {
      this.els.lockRing.classList.toggle('locked', lock.locked);
      this.els.lockRing.classList.toggle('loading', loading);
      this.els.lockRing.classList.toggle('seeking', !lock.locked && lock.progress > 0);
      this.els.lockArc.style.strokeDasharray = `${(lock.progress * 100).toFixed(1)} 100`;
      // The refusal cue outlives the keypress just long enough to be read.
      const warn = frame.lockWarnAt !== null
        && frame.snapshot.time - frame.lockWarnAt < 1.1 && !lock.locked;
      this.els.lockRing.classList.toggle('warn', warn);
    }

    // With the torpedo armed the panel reports what the lock is building on; the other
    // weapons keep the Q/E selection.
    const panelId = torpedoArmed && lock.targetId !== null
      ? lock.targetId
      : frame.snapshot.selectedId;
    const target = frame.snapshot.vessels.find((v) => v.id === panelId);
    const body = target
      ? targetPanel(target, frame.lead?.flightTime ?? null)
      : '<div class="field"><span class="k">target</span><span class="v">none selected</span></div>';

    // Aim orientation lives in the sight picture because the utility button that toggles
    // it cannot be clicked while the scope holds the pointer — the key is the only route.
    const aim = `<div class="field${frame.inverted ? ' flagged' : ''}">`
      + '<span class="k">aim</span>'
      + `<span class="v mono">${frame.inverted ? 'inverted' : 'normal'}`
      + '<span class="key">I</span></span></div>';

    /*
     * The scope captures the mouse, so nothing on the HUD can be clicked while it holds
     * it. Saying so — and naming the way out — is the difference between a deliberate
     * control scheme and an apparently broken interface.
     *
     * It is also purely a mouse problem. Touch never takes the pointer lock, so on a
     * phone this field reported a state that cannot occur and offered a key that is not
     * on the device; it goes entirely rather than being reworded.
     */
    const cursor = frame.touch ? '' : '<div class="field"><span class="k">cursor</span>'
      + `<span class="v mono">${frame.pointerLocked ? 'aiming' : 'free'}`
      + `<span class="key">${frame.pointerLocked ? 'c' : 'click'}</span></span></div>`;

    this.els.scopeInfo.innerHTML = body + aim + cursor;
  }

  /**
   * The per-weapon sight pictures. Everything angular goes through `toSvg`, which maps a
   * true angle to viewBox units at the current magnification: the viewBox square spans
   * the viewport's short side, which is the camera's vertical FOV, so a mark placed with
   * it subtends its real angle on screen at every zoom. That mapping is what makes the
   * spread ring and the ladder instruments rather than art.
   */
  private updateReticle(frame: HudFrame): void {
    const rig = frame.snapshot.rig;
    const mag = frame.magnification;
    const halfTan = Math.tan((scopeFovDeg(mag) * Math.PI) / 360);
    const toSvg = (rad: number): number => (50 * Math.tan(rad)) / halfTan;

    const weapon = rig.selectedWeapon;
    this.ret.g30.classList.toggle('armed', weapon === 'autocannon');
    this.ret.torp.classList.toggle('armed', weapon === 'torpedo');
    this.ret.dc.classList.toggle('armed', weapon === 'depthCharge');

    // Confirmed-hit flick: restart the animation once per new hit timestamp.
    if (frame.hullHitAt !== this.lastHitSeen) {
      this.lastHitSeen = frame.hullHitAt;
      if (frame.hullHitAt !== null) {
        this.ret.base.classList.remove('hit');
        void this.root.offsetWidth; // reflow, so consecutive hits restart the flick
        this.ret.base.classList.add('hit');
      }
    }

    if (mag !== this.lastMag) {
      this.lastMag = mag;
      this.layoutAngularFurniture(toSvg);
    }

    if (weapon === 'autocannon') this.updateGunSight(frame, toSvg);
    this.updateTorpedoSight(frame, toSvg);
    this.updateProjectorSight(frame);
  }

  /** Ladder and windage tick positions — angular, so they move when the zoom does. */
  private layoutAngularFurniture(toSvg: (rad: number) => number): void {
    LADDER.forEach((step, i) => {
      const y = 50 + toSvg(step.holdover);
      const half = step.range % 1000 === 0 ? 2.4 : 1.7;
      const tick = this.ladderTicks[i] as SVGLineElement;
      tick.setAttribute('x1', String(50 - half));
      tick.setAttribute('x2', String(50 + half));
      tick.setAttribute('y1', y.toFixed(2));
      tick.setAttribute('y2', y.toFixed(2));
      const label = this.ladderLabels[i] as SVGTextElement;
      label.setAttribute('x', (50 + half + 0.7).toFixed(2));
      label.setAttribute('y', (y + 0.6).toFixed(2));
    });
    // True 5- and 10-mil marks either side, for crosswind and eyeballed lead.
    ([[-0.01, 1.2], [-0.005, 0.8], [0.005, 0.8], [0.01, 1.2]] as const)
      .forEach(([rad, half], i) => {
        const x = 50 + toSvg(rad);
        const tick = this.windageTicks[i] as SVGLineElement;
        tick.setAttribute('x1', x.toFixed(2));
        tick.setAttribute('x2', x.toFixed(2));
        tick.setAttribute('y1', (50 - half).toFixed(2));
        tick.setAttribute('y2', (50 + half).toFixed(2));
      });
  }

  /** The 30 mm glass: live spread ring, magazine arc, reload and dry states. */
  private updateGunSight(frame: HudFrame, toSvg: (rad: number) => number): void {
    const s = frame.snapshot;
    const rig = s.rig;
    // The ring is the exact cone the next round samples — sustained fire opens it, rough
    // weather sways it, and the barrel cooling draws it back in. Floored at the size of
    // the centre dot so it never collapses into invisibility at 2x.
    this.ret.dispersion.setAttribute(
      'r', Math.max(0.45, toSvg(s.dispersionCone)).toFixed(2),
    );
    this.ret.g30.classList.toggle('hot', rig.dispersionHeat > 0.25);

    const reloading = rig.reloadTimer > 0;
    const dry = !reloading && rig.magazine === 0 && s.resources.ammoReserve === 0;
    // The arc drains with the magazine and sweeps back up through a reload.
    const frac = reloading
      ? 1 - rig.reloadTimer / AUTOCANNON.reloadSeconds
      : rig.magazine / AUTOCANNON.magazine;
    this.ret.magArc.setAttribute('stroke-dasharray', `${(frac * 100).toFixed(1)} 100`);
    this.ret.magCount.textContent = reloading ? '' : String(rig.magazine);
    this.ret.g30.classList.toggle('reloading', reloading);
    this.ret.g30.classList.toggle('dry', dry);
    this.ret.gunState.textContent = reloading ? 'RELOADING' : dry ? 'EMPTY' : '';
  }

  /** The periscope: bearing tape, tube pips, arming warning, and the run line. */
  private updateTorpedoSight(frame: HudFrame, toSvg: (rad: number) => number): void {
    if (frame.snapshot.rig.selectedWeapon !== 'torpedo') {
      this.trackSvg.style.display = 'none';
      return;
    }
    const rig = frame.snapshot.rig;
    const mag = frame.magnification;

    // --- Bearing tape. True positions: a mark sits where its bearing actually is. ----
    const yawDeg = ((frame.cameraYaw * 180) / Math.PI % 360 + 360) % 360;
    this.ret.tapeReadout.textContent =
      `${String(Math.round(yawDeg) % 360).padStart(3, '0')}°`;
    const step = mag >= 6 ? 1 : mag >= 3 ? 2 : 5;
    const halfTan = Math.tan((scopeFovDeg(mag) * Math.PI) / 360);
    const maxDeg = (Math.atan(0.68 * halfTan) * 180) / Math.PI;
    let t = 0;
    let l = 0;
    const first = Math.ceil((yawDeg - maxDeg) / step) * step;
    for (let b = first; b <= yawDeg + maxDeg + 1e-6; b += step) {
      if (t >= this.tapeTicks.length) break;
      const x = 50 + toSvg(((b - yawDeg) * Math.PI) / 180);
      if (x < 16 || x > 84) continue;
      const bearing = ((Math.round(b) % 360) + 360) % 360;
      const major = bearing % 10 === 0;
      const tick = this.tapeTicks[t++] as SVGLineElement;
      tick.setAttribute('visibility', 'visible');
      tick.setAttribute('x1', x.toFixed(2));
      tick.setAttribute('x2', x.toFixed(2));
      tick.setAttribute('y1', '16.5');
      tick.setAttribute('y2', major ? '19.1' : '18.1');
      if (major && l < this.tapeLabels.length) {
        const label = this.tapeLabels[l++] as SVGTextElement;
        label.setAttribute('visibility', 'visible');
        label.setAttribute('x', x.toFixed(2));
        label.setAttribute('y', '21.4');
        label.textContent = String(bearing).padStart(3, '0');
      }
    }
    for (; t < this.tapeTicks.length; t++) {
      (this.tapeTicks[t] as SVGLineElement).setAttribute('visibility', 'hidden');
    }
    for (; l < this.tapeLabels.length; l++) {
      (this.tapeLabels[l] as SVGTextElement).setAttribute('visibility', 'hidden');
    }

    // --- Tube pips: loaded, empty, or the next one sweeping back up from the racks. --
    const racking = rig.torpedoRackTimer > 0;
    this.pipEls.forEach((pip, i) => {
      const fitted = i < rig.torpedoCapacity;
      pip.style.display = fitted ? '' : 'none';
      pip.classList.toggle('loaded', i < rig.torpedoTubes);
      pip.classList.toggle('racking', racking && i === rig.torpedoTubes);
    });

    // --- Arming warning and the run line. --------------------------------------------
    const lock = frame.snapshot.torpedoLock;
    const target = lock.targetId !== null
      ? frame.snapshot.vessels.find((v) => v.id === lock.targetId)
      : undefined;
    /*
     * Inside the arming run the warhead cannot go live before it arrives — the shot the
     * ring is promising would be a dud. Until now the only way to learn the arming
     * distance was to spend a torpedo finding it.
     */
    const tooClose = target !== undefined
      && Math.hypot(target.pos.x - TORPEDO_MOUNT.x, target.pos.z - TORPEDO_MOUNT.z)
        < TORPEDO.armDistance;
    this.els.lockRing.classList.toggle('noarm', tooClose);

    if (lock.locked && target) {
      this.trackSvg.style.display = 'block';
      this.updateTrackLine(frame, target);
    } else {
      this.trackSvg.style.display = 'none';
    }
  }

  /**
   * The run the fish would make, drawn on the water: launched flat along the current
   * aim, gyro correcting toward the locked hull at its real (slow) turn rate, speed
   * building on the real curve. Integrated coarsely all the way to the hull — a first
   * cut drew only the first 300 m and drew nothing, because at scope magnification the
   * near run sits far below the sight picture; it is the terminal leg the glass can
   * actually show. This is the nominal track; the per-fish gyro drift is decided at
   * launch and deliberately not faked here.
   */
  private updateTrackLine(frame: HudFrame, target: Vessel): void {
    const pts: string[] = [];
    let x = TORPEDO_MOUNT.x;
    let z = TORPEDO_MOUNT.z;
    let heading = frame.cameraYaw;
    let speed = TORPEDO.speedStart;
    const accel = (TORPEDO.speedMax - TORPEDO.speedStart) / TORPEDO.accelSeconds;
    const dt = 0.4;
    let travelled = 0;
    for (let i = 0; i < 120 && travelled < TORPEDO.maxRun; i++) {
      const want = Math.atan2(target.pos.x - x, target.pos.z - z);
      let delta = (want - heading) % (Math.PI * 2);
      if (delta > Math.PI) delta -= Math.PI * 2;
      if (delta < -Math.PI) delta += Math.PI * 2;
      const maxTurn = TORPEDO.turnRate * dt;
      heading += Math.max(-maxTurn, Math.min(maxTurn, delta));
      x += Math.sin(heading) * speed * dt;
      z += Math.cos(heading) * speed * dt;
      travelled += speed * dt;
      speed = Math.min(TORPEDO.speedMax, speed + accel * dt);
      const remaining = Math.hypot(target.pos.x - x, target.pos.z - z);
      if (i % 3 === 2 || remaining < 30) {
        const p = frame.project(x, 0.4, z);
        if (p.visible) pts.push(`${p.x.toFixed(1)},${p.y.toFixed(1)}`);
      }
      // Stop at the hull: a plotted run continuing past the target reads as a miss.
      if (remaining < 30) break;
    }
    this.trackLine.setAttribute('points', pts.join(' '));
  }

  /**
   * The mortar sight: fuse scale on the glass, and the impact marker on the water.
   *
   * The marker is the honest landing point — the aim ray from the *mount*, which sits
   * 20 m below the eye and 35 m north of it, so the splash is visibly not the crosshair
   * centre. Green inside the envelope; amber with the reason when the projector would
   * refuse, before the refusal rather than after it.
   */
  private updateProjectorSight(frame: HudFrame): void {
    const s = frame.snapshot;
    if (s.rig.selectedWeapon !== 'depthCharge') {
      this.dcImpact.style.display = 'none';
      return;
    }

    const fuseY = 52.3 + (s.rig.fuseDepth / 45) * 6.4;
    this.fuseChevron.setAttribute('transform', `translate(0 ${fuseY.toFixed(2)})`);
    DEPTH_CHARGE.fuseDepths.forEach((d, i) => {
      (this.fuseLabelEls[i] as SVGTextElement).classList
        .toggle('cur', d === s.rig.fuseDepth);
    });

    const aim = frame.aimWater;
    this.ret.dc.classList.toggle('nosol', aim === null);
    if (!aim) {
      this.dcImpact.style.display = 'none';
      return;
    }

    const range = Math.hypot(aim.x - PROJECTOR_MOUNT.x, aim.z - PROJECTOR_MOUNT.z);
    const short = range < DEPTH_CHARGE.minRange;
    const long = range > s.projectorMaxRange;
    const p = frame.project(aim.x, 0.5, aim.z);
    if (!p.visible) {
      this.dcImpact.style.display = 'none';
      return;
    }
    this.dcImpact.style.display = 'block';
    this.dcImpact.style.left = `${p.x}px`;
    this.dcImpact.style.top = `${p.y}px`;
    this.dcImpact.classList.toggle('out', short || long);
    this.dcImpact.classList.toggle(
      'cooling', s.rig.projectorCooldown > 0 || s.rig.projectorSwapTimer > 0,
    );
    const r = this.dcImpact.querySelector('.r');
    if (r) r.textContent = `${Math.round(range)} m`;
    const st = this.dcImpact.querySelector('.s');
    if (st) st.textContent = short ? 'TOO CLOSE' : long ? 'MAX RANGE' : '';
  }

  resize(): void {
    this.radar.resize();
  }

  dispose(): void {
    this.root.remove();
  }
}

// --- Helpers --------------------------------------------------------------------------

function targetPanel(v: Vessel, flightTime: number | null): string {
  const spec = VESSEL_SPECS[v.kind];
  const range = Math.hypot(v.pos.x, v.pos.z);
  const identified = v.detection === 'identified';
  const bearing = ((Math.atan2(v.pos.x, v.pos.z) * 180) / Math.PI + 360) % 360;

  const field = (k: string, val: string): string =>
    `<div class="field"><span class="k">${k}</span><span class="v mono">${val}</span></div>`;

  return [
    field('type', identified ? spec.label : 'unidentified'),
    field('range', formatRange(range)),
    field('bearing', `${bearing.toFixed(0).padStart(3, '0')}°`),
    field('speed', identified ? `${(v.speed * 1.944).toFixed(0)} kn` : '—'),
    field('flight', flightTime !== null ? `${flightTime.toFixed(1)} s` : '—'),
    field('hull', identified ? `${Math.max(0, Math.round((v.hp / v.maxHp) * 100))}%` : '—'),
  ].join('');
}

function formatRange(metres: number): string {
  // One decimal is all the precision that means anything at these distances, and two
  // made the scope marker read as a readout rather than a label.
  return metres >= 1000 ? `${(metres / 1000).toFixed(1)} km` : `${Math.round(metres)} m`;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

const TEMPLATE = `
<div id="resourceBar">
  <div class="resource">
    <span class="label">funds</span>
    <span class="value mono" id="resMoney">$0</span>
  </div>
  <div class="resource">
    <span class="label">crude · bbl</span>
    <span class="value mono" id="resOil">0</span>
  </div>
  <div class="resource">
    <span class="label">rig integrity</span>
    <span class="value mono" id="resIntegrity">100%</span>
    <span class="bar"><i id="resIntegrityBar" style="width:100%"></i></span>
  </div>
  <div class="resource">
    <span class="label">crew</span>
    <span class="value mono" id="resCrew">0/0</span>
    <span class="bar"><i id="resCrewBar" style="width:100%"></i></span>
  </div>
  <div class="resource">
    <span class="label">rounds</span>
    <span class="value mono" id="resAmmo">0</span>
  </div>
</div>

<div id="missionBanner">
  <div class="kicker">objective</div>
  <div class="text" id="missionBannerText"></div>
</div>

<div id="radioLog"></div>
<div id="contactLayer"></div>
<div id="weaponNameFlash" role="status" aria-live="polite" aria-atomic="true"></div>

<!-- HORIZON's iron sight. Adam, 2026-07-30: the 30 mm fires in the wide view too, "for
     close contact — like when encountering the tentacles or the eye — the scope view is
     too much". Deliberately tiny and thin: it marks the axis without becoming a sight
     picture, because the wide view's whole cost is that it cannot aim precisely. Hidden
     down the optic, where the real reticle takes over. -->
<div id="wideCross" aria-hidden="true">
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
    <g stroke="rgba(226,236,242,0.5)" stroke-width="0.13" fill="none">
      <line x1="50" y1="47.6" x2="50" y2="49.2"/>
      <line x1="50" y1="50.8" x2="50" y2="52.4"/>
      <line x1="47.6" y1="50" x2="49.2" y2="50"/>
      <line x1="50.8" y1="50" x2="52.4" y2="50"/>
    </g>
    <circle cx="50" cy="50" r="0.17" fill="rgba(226,236,242,0.7)"/>
  </svg>
</div>

<div id="scopeOverlay">
  <div class="vignette"></div>
  <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
    <!-- The shared fine cross. Every weapon fires down this same axis, so the cross is
         common property; everything else on the glass belongs to one weapon's sight
         picture and appears only while that weapon is armed. -->
    <g id="retBase" stroke="rgba(226,236,242,0.82)" stroke-width="0.22" fill="none">
      <line x1="50" y1="30" x2="50" y2="46"/>
      <line x1="50" y1="54" x2="50" y2="70"/>
      <line x1="30" y1="50" x2="46" y2="50"/>
      <line x1="54" y1="50" x2="70" y2="50"/>
      <circle cx="50" cy="50" r="0.5" stroke-width="0.3"/>
      <!-- A confirmed 30 mm hull hit flicks four diagonal ticks around the centre. -->
      <g id="hitTicks" stroke="rgba(255,214,140,0.95)" stroke-width="0.3">
        <line x1="47.2" y1="47.2" x2="48.5" y2="48.5"/>
        <line x1="52.8" y1="47.2" x2="51.5" y2="48.5"/>
        <line x1="47.2" y1="52.8" x2="48.5" y2="51.5"/>
        <line x1="52.8" y1="52.8" x2="51.5" y2="51.5"/>
      </g>
    </g>

    <!-- 30 mm: the gunner's glass. The spread ring is the sim's actual dispersion cone
         at true angular size; the ladder ticks are holdovers computed from the real
         ballistics; the left arc is the magazine draining. All positions are written at
         runtime from the current magnification — nothing here is decoration. -->
    <g id="ret30" class="sight" fill="none">
      <circle id="dispersionRing" cx="50" cy="50" r="2"/>
      <g id="ladder"></g>
      <g id="windage"></g>
      <path id="magArcTrack" d="M 32.64 70.68 A 27 27 0 0 1 32.64 29.32" pathLength="100"/>
      <path id="magArc" d="M 32.64 70.68 A 27 27 0 0 1 32.64 29.32" pathLength="100"/>
      <text id="magCount" x="24.8" y="49.5" text-anchor="middle">1800</text>
      <text id="gunState" x="50" y="61.6" text-anchor="middle"></text>
    </g>

    <!-- Torpedo: the periscope. A true bearing tape across the head of the glass, tube
         pips, and the aim-lock ring — an orbiting seeker while acquiring, an amber dwell
         arc, and on lock the designator diamond with a flashing SHOOT cue. A refused
         unlocked shot flashes LOCK TARGET; a locked hull inside arming range shows
         WON'T ARM instead of promising a kill the fish cannot deliver. -->
    <g id="retTorp" class="sight" fill="none">
      <g id="tape"></g>
      <path id="tapeCaret" d="M 49.3 13.9 L 50.7 13.9 L 50 15.3 Z"/>
      <text id="tapeReadout" x="50" y="12.9" text-anchor="middle">000</text>
      <g id="tubePips"></g>
      <g id="lockRing" fill="none">
        <circle class="lockTrack" cx="50" cy="50" r="6"/>
        <g id="lockSpin">
          <circle class="spinRing" cx="50" cy="50" r="7.6" pathLength="100"
                  stroke-dasharray="5 20"/>
        </g>
        <circle id="lockArc" cx="50" cy="50" r="6" pathLength="100"
                transform="rotate(-90 50 50)"/>
        <path id="lockDiamond" d="M50 42.8 L57.2 50 L50 57.2 L42.8 50 Z"/>
        <g class="lockTicks" stroke-width="0.35">
          <line x1="44.8" y1="44.8" x2="46.6" y2="46.6"/>
          <line x1="55.2" y1="44.8" x2="53.4" y2="46.6"/>
          <line x1="44.8" y1="55.2" x2="46.6" y2="53.4"/>
          <line x1="55.2" y1="55.2" x2="53.4" y2="53.4"/>
        </g>
        <text id="lockText" x="50" y="62.4" text-anchor="middle">SHOOT</text>
        <text id="lockLoad" x="50" y="62.4" text-anchor="middle">LOADING</text>
        <text id="lockWarn" x="50" y="40.6" text-anchor="middle">LOCK TARGET</text>
        <text id="lockNoArm" x="50" y="40.6" text-anchor="middle">WON'T ARM</text>
      </g>
    </g>

    <!-- Depth charge: the mortar sight. The fuse-depth scale lives on the glass because
         depth is this weapon's whole idea; the impact marker itself is a projected world
         point and lives in the contact layer, where the scope's clip applies. -->
    <g id="retDC" class="sight" fill="none">
      <g id="fuseScale"></g>
      <text id="dcHint" x="50" y="40.6" text-anchor="middle">AIM ON WATER</text>
    </g>
  </svg>
  <div id="scopeInfo"></div>
</div>

<div id="zoomControl">
  <span class="mag mono" id="zoomLabel">2×</span>
  <input type="range" id="zoomSlider" min="2" max="8" step="1" value="2" aria-label="Scope magnification"/>
  <span class="zoomKeys">zoom <b>(− =)</b></span>
</div>

<div id="fuseSelector" role="listbox" aria-label="Depth charge fuse"></div>

<button id="fireButton" type="button" aria-label="Hold to fire">
  <span class="fireLabel">fire</span>
  <span class="fireHint">hold to engage</span>
</button>

<div id="radarWrap">
  <canvas id="radar"></canvas>
  <div class="radarKeys">cycle <b>(q e)</b></div>
</div>

<div id="bottomBar">
  <div id="weaponCards">
    <button class="weaponCard active" type="button" data-weapon="autocannon">
      <span class="icon"><img src="${WEAPON_ICONS.autocannon}" alt="" aria-hidden="true"/></span>
      <span class="name">30 mm <b>(1)</b></span>
      <span class="fireKey">space fires</span>
      <span class="ammo mono">1800</span>
      <span class="badge"></span>
    </button>
    <button class="weaponCard" type="button" data-weapon="torpedo">
      <span class="icon"><img src="${WEAPON_ICONS.torpedo}" alt="" aria-hidden="true"/></span>
      <span class="name">torpedo <b>(2)</b></span>
      <span class="ammo mono">2</span>
      <span class="badge"></span>
    </button>
    <button class="weaponCard" type="button" data-weapon="depthCharge">
      <span class="icon"><img src="${WEAPON_ICONS.depthCharge}" alt="" aria-hidden="true"/></span>
      <span class="name">depth chg <b>(3)</b></span>
      <span class="ammo mono">6</span>
      <span class="badge"></span>
    </button>
  </div>

  <div id="viewTabs">
    <button class="viewTab active" type="button" data-view="horizon">horizon <b>(tab)</b></button>
    <button class="viewTab" type="button" data-view="scope">scope <b>(tab)</b></button>
    <!-- A third "dock" tab sat here, permanently disabled and labelled Offline — V2.
         Removed at Adam's direction: a control that has never done anything is not a
         roadmap, it is a dead key taking space from the two that matter. -->

  </div>

  <div id="utilityButtons">
    <!-- Pause has always existed on Escape, which is no help on a phone and undiscoverable
         on a desktop. It gets a control on both. -->
    <button class="utilBtn menu" type="button" id="utilMenu" title="Menu — pauses the game">menu <b>(esc)</b></button>
    <button class="utilBtn" type="button" id="utilSpotlight" title="Spotlight">light <b>(l)</b></button>
    <button class="utilBtn" type="button" id="utilInvert" title="Invert vertical aim">inv look <b>(w)</b></button>
    <button class="utilBtn" type="button" id="utilHorn" title="Foghorn">horn <b>(h)</b></button>
  </div>
</div>

<div id="alarmPulse"></div>
`;
